import { CYCLE_INTERVAL } from '@/config/subscription';
import type { BillingCycle, PlanId, PlanPrice, PlanPrices, SubscriptionStatus } from '@/types';
import type { AuthSession } from './auth.service';

/**
 * The browser half of billing.
 *
 * Every call names a plan and a cycle and nothing else. No amount, no currency
 * and no provider product id is ever sent — the server would ignore them, and
 * sending them would invite the belief that they matter.
 *
 * Checkout is a redirect, not an embedded widget. The server creates a Dodo
 * checkout session and returns one URL; the browser navigates to it. No payment
 * script is injected, no card field is ever rendered by QuotyLab, and returning
 * from that URL proves nothing — entitlement changes only when a signed webhook
 * reaches the server.
 */

export interface Entitlement {
  plan: PlanId;
  status: SubscriptionStatus;
  billingCycle: BillingCycle | null;
  /** Next charge while active; the moment access lapses once cancelled. */
  nextBillingDate: string | null;
  cancelAtNextBillingDate: boolean;
  cancelledAt: string | null;
  /** What this subscription bills per cycle, in minor units — Dodo's figure. */
  amountMinor: number | null;
  currency: string | null;
}

/** What the client assumes when the server declines to vouch for anyone. */
export const FREE_ENTITLEMENT: Entitlement = {
  plan: 'free',
  status: 'none',
  billingCycle: null,
  nextBillingDate: null,
  cancelAtNextBillingDate: false,
  cancelledAt: null,
  amountMinor: null,
  currency: null,
};

/** Short codes from the server, turned into something worth reading. */
const MESSAGES: Record<string, string> = {
  unauthenticated: 'Sign in to manage your subscription.',
  forbidden: 'That subscription belongs to another account.',
  invalid_plan: 'That plan is not available.',
  invalid_cycle: 'Choose monthly or yearly billing.',
  already_subscribed: 'You already have an active subscription.',
  provider_error: 'The payment provider is not responding. Try again shortly.',
  storage_error: 'We could not read your subscription. Contact support.',
  no_customer: 'There is no billing account for you yet.',
  unavailable: 'Billing is not available right now.',
  bad_request: 'Something was wrong with that request.',
};

/**
 * Codes that are an *answer*, not an outage.
 *
 * The server saying "I do not know who you are" is information: it means the
 * session cannot be vouched for, and the caller must fall back to free. A
 * network failure is not information and must not downgrade anyone.
 */
export const FAIL_CLOSED_CODES: ReadonlySet<string> = new Set(['unauthenticated', 'forbidden']);

export class BillingError extends Error {
  constructor(public readonly code: string) {
    super(MESSAGES[code] ?? 'Something went wrong. Try again.');
    this.name = 'BillingError';
  }
}

async function call<T>(path: string, session: AuthSession, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${session.accessToken}`,
        ...(init.headers as Record<string, string> | undefined),
      },
    });
  } catch {
    // Offline, DNS, blocked — indistinguishable from here and equally unactionable.
    throw new BillingError('provider_error');
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new BillingError(body.error ?? 'bad_request');
  }
  return (await response.json()) as T;
}

export const fetchEntitlement = (session: AuthSession): Promise<Entitlement> =>
  call<Entitlement>('/api/subscription/status', session);

/**
 * Asks the server to open a checkout, and returns where to send the browser.
 *
 * The cycle is the entire request. An amount, a currency or a product id sent
 * from here would be ignored, so none is sent — there is nothing in this
 * payload that is worth tampering with.
 */
export const startCheckout = (session: AuthSession, cycle: BillingCycle): Promise<{ checkoutUrl: string }> =>
  call<{ checkoutUrl: string }>('/api/subscription/checkout', session, {
    method: 'POST',
    body: JSON.stringify({ cycle }),
  });

/** A link into Dodo's customer portal — cancellation, cards, invoices. */
export const openPortal = (session: AuthSession): Promise<{ link: string }> =>
  call<{ link: string }>('/api/subscription/portal', session, { method: 'POST' });

/**
 * The current Pro prices, from Dodo by way of the server.
 *
 * Unauthenticated: prices are public, and the landing page shows them to
 * visitors who are not signed in. Nothing is sent — no session, no body — and
 * nothing received here is ever sent back: checkout takes a cycle and looks the
 * product and its price up itself.
 *
 * The answer is checked before it is believed. Anything that is not a well
 * formed price for the right cycle is treated as no answer at all, so the UI
 * says pricing is unavailable rather than rendering a malformed figure.
 */
export async function fetchPlanPrices(): Promise<PlanPrices> {
  let response: Response;
  try {
    response = await fetch('/api/subscription/plans');
  } catch {
    throw new BillingError('provider_error');
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new BillingError(body.error ?? 'bad_request');
  }

  const body = (await response.json().catch(() => null)) as Partial<Record<BillingCycle, unknown>> | null;
  const monthly = readPlanPrice(body?.monthly, 'monthly');
  const yearly = readPlanPrice(body?.yearly, 'yearly');
  if (!monthly || !yearly) throw new BillingError('provider_error');
  return { monthly, yearly };
}

function readPlanPrice(value: unknown, cycle: BillingCycle): PlanPrice | null {
  if (typeof value !== 'object' || value === null) return null;
  const { productId, name, amountMinor, currency, interval } = value as Record<string, unknown>;
  if (typeof productId !== 'string' || productId.length === 0) return null;
  if (name !== null && typeof name !== 'string') return null;
  if (typeof amountMinor !== 'number' || !Number.isInteger(amountMinor) || amountMinor <= 0) return null;
  if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) return null;
  if (interval !== CYCLE_INTERVAL[cycle]) return null;
  return { productId, name, amountMinor, currency, interval: CYCLE_INTERVAL[cycle] };
}
